import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IoInformationCircleOutline } from 'react-icons/io5';

interface InfoBubbleProps {
  message: string;
  className?: string;
  iconOnly?: boolean;
  iconClassName?: string;
  tooltipClassName?: string;
}

export const InfoBubble = ({
  message,
  className = '',
  iconOnly = false,
  iconClassName = '',
  tooltipClassName = '',
}: InfoBubbleProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const iconRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  let hoverTimeout: NodeJS.Timeout;

  // Gestion du survol pour le mode iconOnly
  const handleMouseEnter = () => {
    if (!iconOnly) return;
    clearTimeout(hoverTimeout);
    calculatePosition();
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (!iconOnly) return;
    hoverTimeout = setTimeout(() => setIsOpen(false), 300);
  };

  // Gestion du clic pour le mode iconOnly
  const handleClick = (event: React.MouseEvent) => {
    if (!iconOnly) return;
    event.stopPropagation();
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Fermeture au clic extérieur - toujours appelé mais n'a d'effet que si iconOnly est true
  useEffect(() => {
    if (!iconOnly) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        iconRef.current &&
        !iconRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [iconOnly]);

  // Calcul de la position du tooltip
  const calculatePosition = () => {
    if (!iconOnly || !iconRef.current) return;

    const rect = iconRef.current.getBoundingClientRect();
    const tooltipWidth = 250; // Largeur estimée du tooltip

    let top = rect.bottom + window.scrollY + 5;
    let left = rect.left + window.scrollX - tooltipWidth / 2 + rect.width / 2;

    // Ajustement si le tooltip dépasse à droite
    if (left + tooltipWidth > window.innerWidth) {
      left = window.innerWidth - tooltipWidth - 10;
    }

    // Ajustement si le tooltip dépasse à gauche
    if (left < 10) {
      left = 10;
    }

    // Si pas assez d'espace en bas, on affiche au-dessus
    if (top + 100 > window.innerHeight) {
      top = rect.top + window.scrollY - 100;
    }

    setCoords({ top, left });
  };

  // Animation
  const tooltipVariants = {
    hidden: { opacity: 0, scale: 0.95, y: -5 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, scale: 0.95, y: -5, transition: { duration: 0.15 } },
  };

  // Si on n'utilise pas le mode iconOnly, on affiche le composant original
  if (!iconOnly) {
    return (
      <div
        className={`flex items-center gap-1 rounded-md bg-linko-light/10 px-3 py-2 text-xs text-gray-600 ${className}`}
      >
        <IoInformationCircleOutline className="size-4 shrink-0 text-linko-light" />
        <span className="text-gray-600">{message}</span>
      </div>
    );
  }

  const tooltipContent = isOpen && coords && (
    <motion.div
      ref={tooltipRef}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={tooltipVariants}
      style={{ position: 'absolute', top: coords.top, left: coords.left }}
      className={`pointer-events-auto z-50 ${tooltipClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={e => e.stopPropagation()}
    >
      <div className="w-[250px] rounded-md bg-white p-2 text-xs text-gray-600 shadow-lg ring-1 ring-gray-200">
        {message}
      </div>
    </motion.div>
  );

  return (
    <div className={className}>
      <div
        ref={iconRef}
        className={`cursor-help ${iconClassName}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <IoInformationCircleOutline className="size-4 shrink-0 text-gray-500 hover:text-gray-700" />
      </div>
      {createPortal(
        <AnimatePresence>{tooltipContent}</AnimatePresence>,
        document.body,
      )}
    </div>
  );
};
